import type {
    ChatMessageProps,
    ChatSender,
} from "@albertine/design-system/components/chat";
import { AlbertineServiceAgentSenderId } from "@albertine/types";
import {
    type ChatMessage as ApiChatMessage,
    type ChatMessageAttachment,
    ChatMessageSchema,
    type ChatMessageSender,
} from "@albertine/types/openapi/concierge-v1";
import type { UIMessage } from "@tanstack/ai-react";
import { format } from "date-fns/format";
import { parseISO } from "date-fns/parseISO";
import type { ChatMessageDisplayContext } from "../-data/mockRequest";
import { assertNever } from "./chatErrors";
import { downloadFileAttachment } from "./downloadFileAttachment";

export type ChatUIMessage = UIMessage<readonly [], ApiChatMessage>;

export function apiChatMessagesToUI(messages: ApiChatMessage[]): ChatUIMessage[] {
    return messages.toReversed().map(apiChatMessageToUI);
}

export function apiChatMessageToUI(message: ApiChatMessage): ChatUIMessage {
    return {
        id: message.id,
        role: message.sender.type === "member" ? "user" : "assistant",
        createdAt: parseISO(message.sentAt),
        parts: [
            {
                type: "text",
                content: message.messageHtml,
            },
            {
                type: "structured-output",
                status: "complete",
                data: message,
                partial: message,
                raw: JSON.stringify(message),
            },
        ],
    };
}

export function uiMessageToChatMessageProps(
    message: ChatUIMessage,
    display?: ChatMessageDisplayContext,
    replyTitle = "",
    youLabel = "You",
): ChatMessageProps | null {
    const apiMessage = getApiMessage(message);
    if (apiMessage) {
        return apiChatMessageToChatMessageProps(
            apiMessage,
            display,
            replyTitle,
            youLabel,
        );
    }

    const text = message.parts
        .filter((part) => part.type === "text")
        .map((part) => part.content)
        .join("");

    if (message.role === "system" || text.length === 0) {
        return null;
    }

    const sender = mapUIRole(message.role);
    return {
        id: message.id,
        sender,
        sentAt: format(message.createdAt ?? new Date(), "HH:mm"),
        message: text,
        ...displayFieldsForSender(sender, display, undefined, undefined, youLabel),
    };
}

export function apiChatMessageToChatMessageProps(
    message: ApiChatMessage,
    display?: ChatMessageDisplayContext,
    replyTitle = "",
    youLabel = "You",
): ChatMessageProps {
    const attachments: NonNullable<ChatMessageProps["attachments"]> = [];
    const articles: NonNullable<ChatMessageProps["articles"]> = [];

    for (const attachment of message.attachments) {
        switch (attachment.type) {
            case "file":
                attachments.push(mapFileAttachment(attachment));
                break;
            case "catalog-item":
                articles.push({
                    id: attachment.id,
                    title: attachment.name,
                    subtitle: attachment.keyDetail ?? undefined,
                    imageUrl: attachment.imageUrl ?? undefined,
                });
                break;
            default:
                assertNever(attachment);
        }
    }

    const confirmedBookings = message.confirmedBookings ?? [];
    const sender = mapApiSender(message.sender);
    return {
        id: message.id,
        sender,
        avatarUrl:
            message.sender.type === "agent" ? message.sender.avatarUrl : undefined,
        useServiceEmblem:
            message.sender.type === "agent" &&
            message.sender.agentId === AlbertineServiceAgentSenderId,
        sentAt: format(parseISO(message.sentAt), "HH:mm"),
        messageHtml: message.messageHtml,
        attachments,
        articles,
        proposal: message.proposal
            ? {
                  title: message.proposal.title,
                  bookings: message.proposal.bookings.map((booking) => ({
                      id: booking.id,
                      title: booking.title,
                      imageUrl: booking.imageUrl ?? undefined,
                  })),
              }
            : undefined,
        confirmedBookings:
            confirmedBookings.length > 0
                ? confirmedBookings.map((booking) => ({
                      id: booking.id,
                      title: booking.title,
                      imageUrl: booking.imageUrl ?? undefined,
                      options:
                          booking.options && booking.options.length > 0
                              ? booking.options
                              : undefined,
                  }))
                : undefined,
        reaction: message.memberReaction,
        reply: message.reply
            ? {
                  id: message.reply.id,
                  title: replyTitle,
                  message: message.reply.message,
                  imageUrl: message.reply.imageUrl ?? undefined,
              }
            : undefined,
        ...displayFieldsForSender(
            sender,
            display,
            message.sender.name,
            message.requestTitle,
            youLabel,
        ),
    };
}

function mapFileAttachment(
    attachment: Extract<ChatMessageAttachment, { type: "file" }>,
): NonNullable<ChatMessageProps["attachments"]>[number] {
    const mapped = {
        filename: attachment.filename,
        mimeType: attachment.mimeType,
        url: attachment.url,
    };

    if (
        attachment.mimeType.startsWith("image/") ||
        attachment.mimeType.startsWith("audio/")
    ) {
        return mapped;
    }

    return {
        ...mapped,
        onPress: () =>
            downloadFileAttachment({
                url: attachment.url,
                filename: attachment.filename,
                mimeType: attachment.mimeType,
            }),
    };
}

function displayFieldsForSender(
    sender: ChatSender,
    display: ChatMessageDisplayContext | undefined,
    senderNameFromApi?: string,
    requestTitle?: string | null,
    youLabel = "You",
): Pick<ChatMessageProps, "senderName" | "topic"> {
    const topic =
        sender === "agent" ? requestTitle || display?.topic || undefined : undefined;

    if (!display && !senderNameFromApi && !topic) {
        return {};
    }

    return {
        senderName:
            sender === "agent" ? (senderNameFromApi ?? display?.agentName) : youLabel,
        topic,
    };
}

function getApiMessage(message: ChatUIMessage): ApiChatMessage | null {
    for (const part of message.parts) {
        if (part.type === "structured-output" && part.status === "complete") {
            const result = ChatMessageSchema.safeParse(part.data);
            return result.success ? result.data : null;
        }
    }
    return null;
}

function mapApiSender(sender: ChatMessageSender): ChatSender {
    switch (sender.type) {
        case "agent":
            return "agent";
        case "member":
            return "member";
        default:
            return assertNever(sender);
    }
}

function mapUIRole(role: ChatUIMessage["role"]): ChatSender {
    switch (role) {
        case "assistant":
            return "agent";
        case "user":
            return "member";
        case "system":
            throw new Error("System messages do not have a chat sender");
        default:
            return assertNever(role);
    }
}

export function createLocalChatMessage(
    id: string,
    sender: ChatMessageSender,
    messageHtml: string,
    attachments: ChatMessageAttachment[] = [],
    sentAt: string = new Date().toISOString(),
): ApiChatMessage {
    return {
        id,
        sendingId: id,
        sender:
            sender.type === "agent"
                ? { ...sender, avatarUrl: sender.avatarUrl ?? null }
                : sender,
        messageHtml,
        attachments,
        sentAt,
        memberReaction: null,
        requestId: null,
        requestTitle: null,
        proposal: null,
        confirmedBookings: [],
        reply: null,
    };
}
